import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  ShieldCheckIcon, 
  Bars3Icon, 
  XMarkIcon, 
  HomeIcon, 
  SunIcon, 
  MoonIcon, 
  MagnifyingGlassIcon,
  GlobeAmericasIcon,
  PowerIcon,
  UserCircleIcon,
  BuildingOffice2Icon,
  RectangleStackIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion'
import { useTema } from '../../contextos/tema/ContextoTema'

interface LayoutInstitucionalProps {
  children: React.ReactNode
}

const LayoutInstitucional: React.FC<LayoutInstitucionalProps> = ({ children }) => {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { usuario, logout } = useAuth()
  const { toggleTema, isDark } = useTema()
  
  const esGestionInstitucional =
    usuario?.tipo_organizacion === 'institucion' &&
    ['jefe', 'maestro', 'admin', 'supervisor'].includes(usuario?.rol_organizacion || '')
  const esUsuarioInstitucional =
    usuario?.tipo_organizacion === 'institucion' &&
    ['usuario', 'alumno'].includes(usuario?.rol_organizacion || '')

  const navItems = [
    ...(esUsuarioInstitucional ? [
      { name: 'Dashboard Operativo', href: '/institucion/usuarios', icon: HomeIcon },
    ] : []),
    ...(esGestionInstitucional ? [
      { name: 'Dashboard Operativo', href: '/institucion/jefes', icon: HomeIcon },
      { name: 'Control Institucional', href: '/institucion/gestion-miembros', icon: ShieldCheckIcon }
    ] : []),
    { name: 'Almacén de Práctica', href: '/institucion/almacen-practica', icon: RectangleStackIcon },
    { name: 'Despacho y Puerto', href: '/institucion/despacho', icon: TruckIcon },
    { name: 'Casos y Simulaciones', href: '/institucion/simulaciones', icon: GlobeAmericasIcon },
    { name: 'Inteligencia Aduanera', href: '/institucion/tarifas/clasificacion', icon: MagnifyingGlassIcon },
  ]

  const isCurrentPath = (href: string) => location.pathname === href

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(241,245,249,1),_transparent_40%),radial-gradient(circle_at_left,_rgba(226,232,240,0.8),_transparent_30%),linear-gradient(180deg,_#ffffff_0%,_#f8fafc_100%)] text-slate-900 transition-colors duration-300 dark:bg-[#000000] dark:bg-none dark:text-zinc-100">
      {/* Mobile Topbar */}
      <div className="fixed left-0 right-0 top-0 z-40 h-14 flex items-center justify-between border-b border-white/70 bg-white/90 px-4 backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000]/80 lg:hidden">
        <div className="flex items-center space-x-2">
          <div className="rounded-md bg-sky-500/10 p-1.5 border border-sky-500/20 dark:border-blue-500/20 dark:bg-blue-500/10">
            <BuildingOffice2Icon className="h-5 w-5 text-sky-600 dark:text-blue-500" />
          </div>
          <span className="font-bold text-xs uppercase tracking-widest text-slate-900 dark:text-white">Lgx / Académico</span>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={toggleTema} 
            className="rounded-md p-1.5 text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-900 transition-colors"
            title={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          >
            {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
          {usuario && (
            <Link to={esGestionInstitucional ? '/institucion/jefes/perfil' : '/institucion/usuarios/perfil'} className="hidden sm:block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-zinc-300">
              {usuario.nombre.split(' ')[0]}
            </Link>
          )}
          <button 
            onClick={logout} 
            className="rounded-md p-1.5 text-slate-600 dark:text-zinc-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            title="Desconectar"
          >
            <PowerIcon className="h-5 w-5" />
          </button>
          <button onClick={() => setSidebarOpen(v => !v)} className="rounded-md p-1.5 text-slate-600 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-900">
            {sidebarOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm lg:hidden dark:bg-black/80"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed left-0 top-0 z-50 h-[calc(100vh)] w-72 transform border-r transition-transform duration-300 lg:sticky lg:h-screen
          ${isDark
            ? 'border-zinc-800/50 bg-[#000000]'
            : 'border-slate-200/60 bg-white/80 backdrop-blur-xl shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)]'
          }
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Logo Area */}
          <div className={`hidden border-b px-6 py-6 lg:flex lg:flex-col ${isDark ? 'border-zinc-800/50' : 'border-slate-200/60'}`}>
            <div className="flex items-center gap-3">
              <div className={`rounded-2xl border p-3 ${
                isDark
                  ? 'border-zinc-800 bg-zinc-900 shadow-none'
                  : 'border-slate-200 bg-white shadow-sm'
              }`}>
                <BuildingOffice2Icon className={`h-6 w-6 ${isDark ? 'text-zinc-100' : 'text-slate-800'}`} />
              </div>
              <div className="flex flex-col">
                <span className={`text-sm font-bold tracking-[0.22em] ${isDark ? 'text-white' : 'text-slate-900'}`}>LOGIXPORT</span>
                <span className={`text-[10px] font-semibold tracking-wider ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>CAMPUS EDITION</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="mt-6 space-y-2 px-4">
            <p className={`mb-2 px-3 text-[11px] font-medium ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
              Tu espacio
            </p>
            {navItems.map(item => {
              const active = isCurrentPath(item.href)
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`
                    group relative flex items-center gap-3 rounded-2xl px-4 py-3 transition-colors duration-200
                    ${active 
                      ? (isDark
                        ? 'bg-zinc-900 text-white'
                        : 'bg-slate-900 text-white shadow-md')
                      : (isDark
                        ? 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`
                    h-5 w-5 transition-colors duration-200
                    ${active
                      ? (isDark ? 'text-white' : 'text-white')
                      : (isDark ? 'text-zinc-500 group-hover:text-zinc-300' : 'text-slate-400 group-hover:text-slate-700')
                    }
                  `} />
                  <span className="truncate text-sm font-medium">{item.name}</span>
                </Link>
              )
            })}
          </nav>

          <div className="mt-8 px-4">
            <div className={`rounded-[24px] border p-4 ${
              isDark
                ? 'border-zinc-800/60 bg-zinc-900/50'
                : 'border-slate-200 bg-white/50'
            }`}>
              <p className={`text-[11px] font-medium ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>Sesión activa</p>
              <div className="mt-3 flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                  isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-100 text-slate-700'
                }`}>
                  <UserCircleIcon className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className={`truncate text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {usuario?.nombre || 'Usuario'}
                  </p>
                  <p className={`truncate text-[11px] ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    {usuario?.rol_organizacion || 'Alumno'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden pt-14 lg:pt-0">
          {/* Topbar / Header */}
          <header className="sticky top-0 z-30 hidden items-center justify-between border-b border-slate-200/60 bg-white/60 px-8 py-4 backdrop-blur-xl dark:border-zinc-800/50 dark:bg-[#000000]/80 lg:flex">
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-[11px] font-medium text-slate-500 shadow-sm dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
                <span className="mr-2 text-slate-300 dark:text-zinc-500">/</span>
                Institución
                <span className="mx-2 text-slate-300 dark:text-zinc-700">/</span>
                <span className="text-slate-900 dark:text-zinc-200">Campus</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={toggleTema} 
                className="rounded-full border border-slate-200 bg-white/80 p-2.5 text-slate-500 shadow-sm transition-colors duration-200 hover:text-slate-800 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                title={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              >
                {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>
              
              <div className="h-6 w-px bg-slate-200/80 dark:bg-zinc-800" />

              {usuario && (
                <Link 
                  to={esGestionInstitucional ? '/institucion/jefes/perfil' : '/institucion/usuarios/perfil'} 
                  className="flex items-center gap-3 rounded-full border border-slate-200 bg-white/80 px-3 py-2 shadow-sm transition-colors duration-200 hover:bg-white dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white dark:bg-zinc-800 dark:bg-none dark:text-zinc-300">
                    <UserCircleIcon className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-xs font-semibold text-slate-700 dark:text-zinc-100 leading-none">
                      {usuario.nombre.split(' ')[0]}
                    </span>
                    <span className="mt-0.5 text-[11px] text-slate-500 dark:text-zinc-400">
                      {usuario.rol_organizacion || 'Operador'}
                    </span>
                  </div>
                </Link>
              )}
              
              <button 
                onClick={logout} 
                className="group flex items-center justify-center rounded-full border border-slate-200 bg-white/80 p-2.5 text-slate-500 shadow-sm transition-colors duration-200 hover:text-red-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:text-red-400"
                title="Desconectar"
              >
                <PowerIcon className="h-5 w-5 transition-transform group-hover:scale-110" />
              </button>
            </div>
          </header>

          {/* Page Content Area */}
          <div className="custom-scrollbar flex-1 overflow-y-auto bg-transparent p-4 lg:p-8">
            <div className="mx-auto max-w-[1480px]">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default LayoutInstitucional
